// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';

const CAMPAIGN_STEPS = [
  { id: 'channel',  label: '1. Channel' },
  { id: 'audience', label: '2. Audience' },
  { id: 'compose',  label: '3. Compose' },
  { id: 'review',   label: '4. Review & send' },
];
import { ChevronLeft, Download, Hash, Loader2, Mail, MessageSquare, Send, Tag, Upload, Users, CalendarDays } from 'lucide-react';
import { EMAIL_TEMPLATES } from '../../data/comms';
import { createCampaign, loadEventPages, previewCampaignAudience, loadAudienceTags } from '../../lib/backOfficeApi';
import { logAudit, toast } from '../../lib/buses';
import { DesignPreviewCard } from './DesignPreviewCard';
import { Field, Input, Textarea } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { EventSelect } from '../../components/filters/EventSelect';
import { WhatsAppTemplatePicker, templateEditableFields } from './WhatsAppTemplatePicker';

// Shows the REAL resolved audience size from the backend preview (loading / number /
// error) — never a fabricated estimate. `exact:false` (multi-source overlap) shows ≈.
const AudienceSizeBar = ({ resolved, idle, idleHint }) => {
  if (idle) return <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-4 py-2.5 text-[11.5px] text-[#9CA3AF]">{idleHint}</div>;
  return (
    <div className="bg-white border border-[#1F2937] rounded-md px-4 py-2.5 flex items-center justify-between">
      <div className="text-[11.5px] text-[#6B7280]">Audience size</div>
      <div className="text-right">
        {resolved.status === 'loading' && <div className="text-[13px] text-[#6B7280] inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Resolving…</div>}
        {resolved.status === 'error' && <div className="text-[12px] text-[#9B1F1F]">Could not resolve size</div>}
        {resolved.status === 'ready' && <>
          <div className="text-[16px] font-semibold tabular-nums text-[#1F2937]">{resolved.exact ? '' : '≈'}{resolved.total.toLocaleString()}</div>
          <div className="text-[10px] text-[#9CA3AF]">recipients · live from real data</div>
        </>}
      </div>
    </div>
  );
};

export const NewCampaignModal = ({ designs, onClose, onSend }) => {
  const [step, setStep] = useState('channel'); // channel | audience | compose | review
  const [channel, setChannel] = useState('email');
  const [audienceSource, setAudienceSource] = useState('event'); // event | segment | upload
  // Event audience (real)
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [audienceType, setAudienceType] = useState('buyers'); // buyers | subscribers | all
  // Saved segment = real subscriber tags
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  // Upload
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadedName, setUploadedName] = useState('');
  // Real resolved audience size from the backend
  const [resolved, setResolved] = useState({ status: 'idle', total: 0, exact: true });
  const [name, setName] = useState('');
  const [emailDesignId, setEmailDesignId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // WhatsApp is TEMPLATE-only for a business-initiated message (Meta's 24h rule), so a
  // WhatsApp campaign carries an approved template + its parameters, not prose.
  const [waTemplate, setWaTemplate] = useState(null); // { name, languageCode, components }
  const [submitting, setSubmitting] = useState(false);

  const channelLabel = channel === 'email' ? 'Email' : channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
  const emailDesigns = designs.filter(d => d.type === 'Email' && !d.archived);
  const linkedDesign = emailDesigns.find(d => d.id === emailDesignId);
  const evName = (id) => { const e = events.find(x => x.id === id); return e?.name || e?.title || id; };

  // Load real events + real subscriber tags once.
  useEffect(() => {
    const ac = new AbortController();
    loadEventPages({ limit: 100, includeStats: false, signal: ac.signal })
      .then(({ rows }) => { if (!ac.signal.aborted) setEvents(rows || []); }).catch(() => {});
    loadAudienceTags({ signal: ac.signal }).then((t) => { if (!ac.signal.aborted) setTags(t || []); }).catch(() => {});
    return () => ac.abort();
  }, []);

  // The real audienceSources for the current selection (mirror the backend resolver).
  const audienceSources = (() => {
    if (audienceSource === 'event' && eventId) return [{ type: 'event', sourceId: eventId, audienceType, sourceName: evName(eventId) }];
    if (audienceSource === 'segment' && selectedTag) return [{ type: 'tag', sourceId: selectedTag, sourceName: selectedTag }];
    return [];
  })();

  // Resolve the REAL audience size from the backend whenever the selection changes.
  useEffect(() => {
    if (audienceSource === 'upload') { setResolved({ status: 'ready', total: uploadedCount, exact: true }); return; }
    if (!audienceSources.length) { setResolved({ status: 'idle', total: 0, exact: true }); return; }
    let alive = true;
    setResolved((r) => ({ ...r, status: 'loading' }));
    const t = setTimeout(() => {
      previewCampaignAudience(audienceSources, { channel })
        .then((d) => { if (alive) setResolved({ status: 'ready', total: d.total || 0, exact: d.exact !== false }); })
        .catch(() => { if (alive) setResolved({ status: 'error', total: 0, exact: true }); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [audienceSource, eventId, audienceType, selectedTag, uploadedCount, channel]); // eslint-disable-line

  const audience = {
    label: audienceSource === 'event' ? `${evName(eventId)} · ${audienceType}` : audienceSource === 'segment' ? `Segment: ${selectedTag}` : `Uploaded list — ${uploadedName || 'contacts.csv'}`,
    sources: audienceSources,
  };
  const audienceSize = resolved.total || 0;
  const hasValidAudience = audienceSource === 'upload' ? uploadedCount > 0 : (audienceSources.length > 0 && resolved.status === 'ready' && audienceSize > 0);

  const canCompose = hasValidAudience && name.trim();
  // WhatsApp's payload is the TEMPLATE, not `body` — gating it on prose would have blocked
  // Review on a campaign that is complete, and (before the picker existed) let one through
  // with no template at all.
  const canReview = canCompose && (channel === 'whatsapp'
    ? !!waTemplate?.name
    : (body.trim() && (channel !== 'email' || subject.trim())));

  // ---- CSV template download (data URL) ----
  const downloadTemplate = () => {
    const headers = 'firstName,lastName,phone,email,country,city,language\n';
    const sampleRow = 'Mariam,Kintu,+256770123456,mariam@example.com,UG,Kampala,English\n';
    const csv = headers + sampleRow;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'tkt-campaign-contacts-template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast('Template downloaded', 'success', 'tkt-campaign-contacts-template.csv');
  };
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const count = Math.max(0, lines.length - 1); // minus header
      setUploadedCount(count);
      setUploadedName(file.name);
      toast('Contacts uploaded', 'success', `${count.toLocaleString()} rows · ${file.name}`);
    };
    reader.readAsText(file);
  };

  const send = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Persist the campaign to the backend as a DRAFT. Executing it (the real
      // blast to live contacts) is a deliberate, separate action — we never
      // auto-send to real people from here.
      const created = await createCampaign({
        name: name.trim(),
        channel, // backend enum: email | whatsapp | sms | push | billboard
        ...(audienceSource === 'event' && eventId ? { eventId, scope: 'event' } : { scope: 'global' }),
        subject: channel === 'email' ? (subject || undefined) : undefined,
        content: channel === 'whatsapp' ? undefined : (body || undefined),
        // The three columns the blast executor actually reads for WhatsApp. Sending them is
        // what makes the campaign send the message the operator composed.
        ...(channel === 'whatsapp' && waTemplate ? {
          templateName: waTemplate.name,
          templateLanguage: waTemplate.languageCode,
          templateComponents: waTemplate.components || [],
        } : {}),
        // REAL audienceSources the backend resolver targets at execute time (event
        // buyers/subscribers or a subscriber tag) — not a fabricated size.
        audience: { audienceSources: audience.sources, _totalResolved: audienceSize, label: audience.label, source: audienceSource },
      });
      const newCampaign = {
        id: created?.id || ('CMP-' + new Date().getFullYear()),
        name: created?.name || name.trim(),
        channel: channelLabel,
        audience: audience.label,
        audienceSize,
        sent: 0, opened: 0, clicks: 0,
        status: 'Draft',
        when: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + new Date().toTimeString().slice(0, 5),
        by: 'You',
        raw: created,
      };
      onSend(newCampaign);
      logAudit({ action: 'Campaign created', target: name.trim(), detail: `${channelLabel} · ${audienceSize.toLocaleString()} recipients · draft` });
      toast('Campaign created', 'success', `${name.trim()} · saved as draft · execute it to send the blast`);
    } catch (err) {
      toast('Campaign not created', 'error', err?.message || 'Backend rejected the request');
    } finally {
      setSubmitting(false);
    }
  };

  const STEPS = CAMPAIGN_STEPS;

  return (
    <Modal open={true} onClose={onClose} width="max-w-[820px]" title="New campaign" sub="Send a broadcast to a list of people. The blast goes out immediately once you send.">
      <div className="flex items-center gap-0 mb-4 border-b border-[#EEEDEA] -mx-5 px-5 pb-0">
        {STEPS.map(s => (
          <div key={s.id} className={`px-3 py-2 text-[11.5px] font-medium border-b-2 ${step === s.id ? 'border-[#1F2937] text-[#1F2937]' : 'border-transparent text-[#9CA3AF]'}`}>{s.label}</div>
        ))}
      </div>

      {step === 'channel' && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: 'email',    l: 'Email',    d: 'Designed templates, subject lines, full content', i: Mail },
              { k: 'whatsapp', l: 'WhatsApp', d: 'High open rates, fastest delivery in Uganda',     i: MessageSquare },
              { k: 'sms',      l: 'SMS',      d: 'Plain text, 160 chars, universal reach',          i: Hash },
            ].map(opt => (
              <button key={opt.k} onClick={() => setChannel(opt.k)} className={`text-left bg-white border rounded-md p-3 transition-colors ${channel === opt.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:border-[#D8D5CF]'}`}>
                <div className="w-8 h-8 rounded-md bg-[#F2F1ED] flex items-center justify-center mb-2"><opt.i className="w-4 h-4 text-[#4B5563]" strokeWidth={1.75} /></div>
                <div className="text-[12.5px] font-semibold text-[#1F2937]">{opt.l}</div>
                <div className="text-[10.5px] text-[#6B7280] mt-0.5 leading-snug">{opt.d}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'audience' && (
        <div className="space-y-3">
          {/* Source picker */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: 'event',   l: 'Event audience', d: 'Buyers or subscribers of an event', i: CalendarDays },
              { k: 'segment', l: 'Saved segment',  d: 'A subscriber tag segment',          i: Tag },
              { k: 'upload',  l: 'Upload contacts', d: 'CSV of custom contacts',           i: Upload },
            ].map(opt => (
              <button key={opt.k} onClick={() => setAudienceSource(opt.k)} className={`text-left bg-white border rounded-md p-2.5 transition-colors ${audienceSource === opt.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:border-[#D8D5CF]'}`}>
                <div className="flex items-center gap-2 mb-1"><opt.i className="w-3.5 h-3.5 text-[#4B5563]" strokeWidth={1.75} /><div className="text-[12px] font-semibold text-[#1F2937]">{opt.l}</div></div>
                <div className="text-[10.5px] text-[#6B7280] leading-snug">{opt.d}</div>
              </button>
            ))}
          </div>

          {/* — EVENT AUDIENCE (real) — */}
          {audienceSource === 'event' && (
            <div className="space-y-2.5">
              <Field label="Event" required>
                <EventSelect events={events} value={eventId} onChange={(id) => setEventId(id)} placeholder="— Pick an event —" className="w-full" />
              </Field>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Who to reach</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { k: 'buyers', l: 'Buyers', d: 'People who bought tickets' },
                    { k: 'subscribers', l: 'Subscribers', d: 'Followers / waitlist' },
                    { k: 'all', l: 'Everyone', d: 'Buyers + subscribers' },
                  ].map(o => (
                    <button key={o.k} onClick={() => setAudienceType(o.k)} className={`text-left border rounded-md p-2.5 ${audienceType === o.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                      <div className="text-[12px] font-semibold text-[#1F2937]">{o.l}</div>
                      <div className="text-[10px] text-[#6B7280]">{o.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              <AudienceSizeBar resolved={resolved} idle={!eventId} idleHint="Pick an event to resolve the real audience size" />
            </div>
          )}

          {/* — SAVED SEGMENT = real subscriber tags — */}
          {audienceSource === 'segment' && (
            <div className="space-y-2">
              {tags.length === 0 ? (
                <div className="text-center py-8 text-[12px] text-[#9CA3AF] bg-[#FBFAF7] border border-[#E6E4E1] rounded-md">No subscriber tags yet. Tags applied to subscribers become reusable segments here.</div>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                  {tags.map(t => (
                    <label key={t.name} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded border cursor-pointer ${selectedTag === t.name ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                      <div className="flex items-center gap-2.5 min-w-0"><input type="radio" name="tag" checked={selectedTag === t.name} onChange={() => setSelectedTag(t.name)} /><span className="text-[12.5px] font-medium text-[#1F2937] inline-flex items-center gap-1.5"><Tag className="w-3 h-3 text-[#9CA3AF]" />{t.name}</span></div>
                      <span className="text-[11px] font-mono tabular-nums text-[#6B7280]">{Number(t.count).toLocaleString()} subscribers</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedTag && <AudienceSizeBar resolved={resolved} idle={false} idleHint="" />}
            </div>
          )}


          {/* — UPLOAD — */}
          {audienceSource === 'upload' && (
            <div className="space-y-3">
              <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-4">
                <div className="text-[11.5px] text-[#1F2937] font-medium mb-1">1. Download the CSV template</div>
                <div className="text-[10.5px] text-[#6B7280] mb-2">Required columns: <span className="font-mono">firstName, lastName, phone, email, country, city, language</span></div>
                <button onClick={downloadTemplate} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] border border-[#E6E4E1] bg-white hover:bg-[#F2F1ED] inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />Download template (CSV)</button>
              </div>
              <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-4">
                <div className="text-[11.5px] text-[#1F2937] font-medium mb-1">2. Fill it in and upload</div>
                <div className="text-[10.5px] text-[#6B7280] mb-2">Max 50,000 contacts per upload · phone in E.164 (+256…) · we de-dupe against existing People</div>
                <label className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] text-white inline-flex items-center gap-1.5 cursor-pointer">
                  <Upload className="w-3.5 h-3.5" />Upload CSV
                  <input type="file" accept=".csv,text/csv" onChange={handleUpload} className="hidden" />
                </label>
                {uploadedCount > 0 && (
                  <div className="mt-3 bg-white border border-[#E6E4E1] rounded-md px-3 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-[12px] font-medium text-[#1F2937]">{uploadedName}</div>
                      <div className="text-[10.5px] text-[#6B7280] tabular-nums">{uploadedCount.toLocaleString()} contacts loaded</div>
                    </div>
                    <button onClick={() => { setUploadedCount(0); setUploadedName(''); }} className="text-[11px] text-[#9B1F1F] hover:underline">Remove</button>
                  </div>
                )}
              </div>
              <div className="bg-[#FEF7E6] border border-[#FDE68A] rounded-md p-2.5 text-[10.5px] text-[#8A5A12]">
                Uploaded contacts are treated as opted-in for this campaign. Make sure you have valid consent before sending.
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'compose' && (
        <div className="space-y-3">
          <Field label="Campaign name" required sub="Internal name · not seen by recipients"><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. AFCON Qualifier · 24h reminder" /></Field>
          {channel === 'email' && (
            <>
              <Field label="Use an email design" sub="Pre-built blocks, colors, and layout from the Designs catalog">
                <select value={emailDesignId} onChange={e => { setEmailDesignId(e.target.value); const d = emailDesigns.find(x => x.id === e.target.value); if (d) { setSubject(d.subject || (d.text && d.text.headline) || ''); setBody((d.text && d.text.subheadline) || ''); } }} className="w-full h-9 bg-white border border-[#E6E4E1] rounded-md px-2.5 text-[12.5px]">
                  <option value="">— No design · plain compose —</option>
                  {emailDesigns.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="Subject line" required><Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Your tickets are ready" /></Field>
            </>
          )}
          {channel === 'whatsapp' ? (
            /* A WhatsApp blast names an APPROVED TEMPLATE. This step used to be a free-text
               body — which Meta rejects for anyone outside the 24-hour window (i.e. an entire
               marketing list), and which the blast executor never read anyway: it sent
               `campaign.templateName || 'welcome_to_conference_2'`, a template used once in
               the platform's history. The operator saw the words they wrote and the audience
               would have received a stranger's conference welcome. */
            <Field label="WhatsApp template" required sub="Meta only accepts an approved template for a business-initiated message — free text is delivered only to someone who messaged you in the last 24 hours, which a marketing list is not.">
              <WhatsAppTemplatePicker value={waTemplate} onChange={setWaTemplate} variables />
            </Field>
          ) : (
            <>
              <Field label={channel === 'email' ? 'Body / preview text' : 'Message body'} required sub={channel === 'sms' ? 'Max 160 characters · plain text' : 'Use variables: {firstName} {eventName} {ticketRef}'}>
                <Textarea rows={channel === 'sms' ? 3 : 6} value={body} onChange={e => setBody(e.target.value)} maxLength={channel === 'sms' ? 160 : undefined} placeholder={channel === 'sms' ? 'TKT: Your tickets for {eventName} on {date} are ready. Open: tkt.ug/t/{ticketRef}' : 'Hi {firstName},\n\nThis is a quick reminder that…'} />
              </Field>
              {channel === 'sms' && <div className="text-[10.5px] text-[#9CA3AF] tabular-nums">{body.length} / 160 chars</div>}
            </>
          )}
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-3">
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-4">
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-[12px]">
              <dt className="text-[#6B7280]">Channel</dt><dd className="text-[#1F2937]">{channelLabel}</dd>
              <dt className="text-[#6B7280]">Audience</dt><dd className="text-[#1F2937]">{audience?.label} <span className="text-[#9CA3AF]">· {audienceSize.toLocaleString()} people · source: {audienceSource}</span></dd>
              <dt className="text-[#6B7280]">Campaign</dt><dd className="text-[#1F2937]">{name}</dd>
              {channel === 'email' && <><dt className="text-[#6B7280]">Subject</dt><dd className="text-[#1F2937]">{subject}</dd></>}
              {channel === 'whatsapp' && <><dt className="text-[#6B7280]">Template</dt><dd className="text-[#1F2937] font-mono">{waTemplate?.name} <span className="text-[#9CA3AF] font-sans">· {waTemplate?.languageCode}</span></dd></>}
              {channel === 'email' && linkedDesign && <><dt className="text-[#6B7280]">Email design</dt><dd className="text-[#1F2937]">{linkedDesign.name} · {linkedDesign.id}</dd></>}
            </dl>
          </div>
          {channel === 'email' && linkedDesign ? (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Preview</div>
              <DesignPreviewCard kind="email" template={{ ...EMAIL_TEMPLATES.find(t => t.id === linkedDesign.templateId), headerColor: linkedDesign.headerColor, accentColor: linkedDesign.accentColor }} fields={linkedDesign.templateDefaults} text={{ ...linkedDesign.text, headline: subject, subheadline: body }} size="md" />
            </div>
          ) : (
            <div className="bg-white border border-[#E6E4E1] rounded-md p-4">
              <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Preview · {channelLabel}</div>
              {channel === 'email' && <div className="text-[12.5px] font-semibold text-[#1F2937] mb-2">{subject}</div>}
              {channel === 'whatsapp' ? (
                /* Show the PARAMETERS that will actually go out. The approved body copy lives
                   with Meta, not here, so this deliberately shows what we control and says so
                   — rather than rendering `body`, which for WhatsApp is now never sent. */
                <div className="space-y-1.5">
                  {templateEditableFields(waTemplate?.components).map((f) => (
                    <div key={f.path.join('.')} className="flex gap-2 text-[12px]">
                      <span className="text-[#9CA3AF] capitalize min-w-[120px]">{f.label}</span>
                      <span className="text-[#374151] break-all">{f.value || <span className="text-[#9CA3AF]">empty</span>}</span>
                    </div>
                  ))}
                  {templateEditableFields(waTemplate?.components).length === 0 && (
                    <div className="text-[12px] text-[#9CA3AF]">No fillable parameters — sends exactly as Meta approved it.</div>
                  )}
                  <div className="text-[10.5px] text-[#9CA3AF] pt-1.5">The message wording itself is the approved <span className="font-mono">{waTemplate?.name}</span> template held by Meta; only these values are set here.</div>
                </div>
              ) : (
                <div className="text-[12px] text-[#374151] whitespace-pre-wrap">{body}</div>
              )}
            </div>
          )}
          <div className="bg-[#FEF7E6] border border-[#FDE68A] rounded-md p-3 text-[11.5px] text-[#8A5A12]">
            Pressing <span className="font-semibold">Send blast</span> will queue the message to <span className="font-semibold">{audienceSize.toLocaleString()}</span> recipients. This action runs immediately and cannot be undone.
          </div>
        </div>
      )}

      <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 flex items-center justify-between">
        {step !== 'channel' ? (
          <button onClick={() => setStep(step === 'audience' ? 'channel' : step === 'compose' ? 'audience' : 'compose')} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5" />Back</button>
        ) : <div />}
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
          {step === 'channel'  && <button onClick={() => setStep('audience')} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] text-white">Continue</button>}
          {step === 'audience' && <button onClick={() => setStep('compose')} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] text-white">Continue</button>}
          {step === 'compose'  && <button onClick={() => setStep('review')} disabled={!canReview} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white">Review &amp; send</button>}
          {step === 'review'   && <button onClick={send} disabled={submitting} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />{submitting ? 'Creating…' : 'Create campaign'}</button>}
        </div>
      </div>
    </Modal>
  );
};

/* ============================================================================
   PAGE: DESIGN & COMMS — mirrors the reference screenshot
   ============================================================================ */

